import {
	type FoxItField,
	FoxItFieldFlags,
	FoxItFieldTypes,
	type FoxItPDFForm,
	type SignaturePlacement,
} from '@/FoxItTypes'
import { DocumentType } from '@/enums/DocumentType'
import type { MissingSignature } from '@/types'
import { DateTime } from 'luxon'

// There are forms that can have multiple signatures with the same name (ex: purchaserSignature1 and purchaserSignature2).
// This regex is used to capture the signature name and the optional number group that can be used to differentiate between them.
const SIGNATURE_NAME_REGEX = /^(\w+?Signature)(\d*)/i

/**
 * This helper takes a form, gathers all the unsigned signature and their correlated date fields
 * Returned fields have coordinates which is used for inform FoxIt where to place a field for e-sign
 * Signatures here has a placement prop, used for removign the sign buttons, and add sig fields back to the pdf
 * @param pdfForm
 * @returns {signatures, dateFields}
 */
// Helper to gather all the unsigned signature buttons's location to add them back after flattening
export const saveUnsignedSignaturePlacementInfo = async (
	pdfForm: FoxItPDFForm
) => {
	const signatures: Array<{
		converted: MissingSignature
		signaturePlacement: SignaturePlacement
	}> = []
	const sigDateFieldRegex = /Signature(\d*)Date/i
	const dateFields: Array<MissingSignature> = []

	for (const field of pdfForm.fields) {
		if (field.info.type === FoxItFieldTypes.button) {
			signatures.push(await convertSignaturePlacementToMissingSignature(field))
		}

		if (sigDateFieldRegex.test(field.info.name)) {
			dateFields.push(
				(await convertSignaturePlacementToMissingSignature(field)).converted
			)
		}
	}

	return { signatures, dateFields }
}

export const convertSignaturePlacementToMissingSignature = async (
	field: FoxItField
): Promise<{
	converted: MissingSignature
	signaturePlacement: SignaturePlacement
}> => {
	const buttonControl = field.getControlByIndex(0)
	const widget = await buttonControl.getWidgetAnnot()
	const page = widget.getPage()
	const rect = widget.getRect()
	const pageIndex = page.getIndex()
	const pageHeight = page.getHeight()
	const convertToPixel = (points: number): number => {
		// We are using 72 as the FoxIt DPI standard (Mark confirmed that it works in the POC)
		const pointToInch = 72
		const DPI = 72
		return Math.round((points / pointToInch) * DPI)
	}

	// Foxit measure the y from the bottom of the page but we want to know the y from the top of the page
	const yFromTop = (y: number, pageHeight: number): number => {
		return pageHeight - y
	}

	const fieldName = field.info.name
	const dateRegex = /Date/i
	const isDateField = dateRegex.test(fieldName)

	return {
		converted: {
			type: isDateField ? 'date' : 'signature',
			x: convertToPixel(rect.left),
			y: convertToPixel(yFromTop(rect.top, pageHeight)),
			width: convertToPixel(rect.right - rect.left),
			height: convertToPixel(rect.top - rect.bottom),
			pageNumber: pageIndex + 1,
			name: fieldName,
			documentNumber: 1,
		},
		signaturePlacement: {
			fieldName,
			pageIndex,
			rect,
		},
	}
}

// Helper function to normalize signature field names
// Ex: signatureName_1a2b3c4d -> signatureName
export const getNormalizedSignatureFieldName = (signatureFieldName: string) => {
	const signatureResults = signatureFieldName.match(SIGNATURE_NAME_REGEX)

	const signatureName = signatureResults?.[1]
	const signatureNumber = signatureResults?.[2]

	if (!signatureResults) {
		return
	}

	return signatureNumber ? `${signatureName}${signatureNumber}` : signatureName
}

// Sets a matching date field following a signing process
export const setSignatureDateField = (
	form: FoxItPDFForm,
	signatureFieldName: string,
	content: string,
	formName: DocumentType | undefined = undefined
) => {
	const insuredSignatureDateOnForm = form.fields.some((field) =>
		field.info.name.includes('insuredSignatureDate')
	)

	if (formName === DocumentType.EnrollmentForm && !insuredSignatureDateOnForm) {
		manageDateWithInsuredOverwrite(content, form, signatureFieldName)
	}

	// The Preneed Contract for NC has the funeral home representative signature date fields split up into 3 fields
	if (
		formName === DocumentType.PreneedContractGuaranteed &&
		signatureFieldName.includes('funeralHomeRepresentativeSignature')
	) {
		manageSplitSignatureDate(
			content,
			form,
			'funeralHomeRepresentativeSignature'
		)
		return
	}

	// Some forms (e.g. the TX G&S) split a signature's date into Day, Month and LastTwoDigitsYear fields
	const normalizedFieldName =
		getNormalizedSignatureFieldName(signatureFieldName)

	if (
		normalizedFieldName &&
		form.fields.some((field) =>
			field.info.name.startsWith(`${normalizedFieldName}Day`)
		)
	) {
		manageSplitSignatureDate(content, form, normalizedFieldName)
		return
	}

	manageSignatureDate(signatureFieldName, content, form)
}

// Helper to set the field for a signature related field (date, time, etc.)
export const setSignatureRelatedTextField = (
	form: FoxItPDFForm,
	signatureBase: string,
	suffix: string,
	content: string
) => {
	if (signatureBase === undefined) {
		return
	}

	const fieldName = `${signatureBase}${suffix}`
	const fields = form?.fields.filter((field) =>
		new RegExp(fieldName).test(field.info.name)
	)
	const flag =
		content === ''
			? FoxItFieldFlags.TextDoNotSpellCheck
			: FoxItFieldFlags.ReadOnly

	fields.forEach((field) => {
		field?.setValue(content)
		field?.setFlags(flag)
	})
}

// Helper to manage the date field for a signature
const manageSignatureDate = (
	signatureFieldName: string,
	content: string,
	form: FoxItPDFForm
) => {
	const normalizedFieldName =
		getNormalizedSignatureFieldName(signatureFieldName)

	if (!normalizedFieldName) {
		return
	}

	setSignatureRelatedTextField(form, normalizedFieldName, 'Date', content)
}

// Helper to manage a signature date that is split into Day, Month and LastTwoDigitsYear fields
const manageSplitSignatureDate = (
	content: string,
	form: FoxItPDFForm,
	signatureBase: string
) => {
	const isContentEmpty = content === ''
	const date = DateTime.now()

	setSignatureRelatedTextField(
		form,
		signatureBase,
		'Day',
		isContentEmpty ? '' : date.day.toString()
	)
	setSignatureRelatedTextField(
		form,
		signatureBase,
		'Month',
		isContentEmpty ? '' : date.monthLong.toString()
	)
	setSignatureRelatedTextField(
		form,
		signatureBase,
		'LastTwoDigitsYear',
		isContentEmpty ? '' : date.year.toString().slice(2)
	)
}

// Most enrollment forms share one date field for insured and purchaser signatures.
// This method will set the purchaserDateField with the date when either signatures are signed
// or it will clear it if both insured and purchaser signatures are removed.
const manageDateWithInsuredOverwrite = (
	content: string,
	form: FoxItPDFForm,
	signatureFieldName: string
) => {
	signatureFieldName = signatureFieldName.replace('insured', 'purchaser')
	const signatureResults = signatureFieldName.match(SIGNATURE_NAME_REGEX)

	if (!signatureResults) {
		return
	}

	const signatureName = signatureResults[1]

	if (signatureName === 'purchaserSignature' && content === '') {
		const bothSignaturesMissing =
			form.fields.filter((field) => {
				return (
					field.info.type === FoxItFieldTypes.button &&
					(field.info.name.includes('insuredSignature') ||
						field.info.name.includes('purchaserSignature'))
				)
			}).length === 2

		if (!bothSignaturesMissing) {
			return
		}
	}

	setSignatureRelatedTextField(form, signatureName, 'Date', content)
}

export const isFuneralDirectorSignature = (signatureFieldName: string) => {
	const fdRegex = /funeralHomeRepresentative/i
	return fdRegex.test(signatureFieldName)
}
